import type { PageDefinition } from '@/types/page'
import { usersPage, familiesPage, regionsPage } from './page-defs/users'
import {
  medicationPlansPage,
  medicationRecordsPage,
  healthMonitoringPage,
  healthAlertsPage,
  alertRulesPage,
  healthReportsPage,
  careTeamPage,
  devicesPage,
} from './page-defs/care'
import { drugsPage, drugReviewPage } from './page-defs/drugs'
import {
  messagesPage,
  notificationTemplatesPage,
  contentTipsPage,
  contentFaqPage,
} from './page-defs/content'
import {
  membershipPlansPage,
  membershipOrdersPage,
  linksPage,
  assetsPage,
} from './page-defs/commerce'
import { supportTicketsPage, supportFeedbackPage } from './page-defs/support'
import {
  adminUsersPage,
  rolesPage,
  auditLogsPage,
  generalSettingsPage,
  dashboardPage,
  analyticsPage,
  operationsPage,
  profilePage,
  decorationPage,
  loginPage,
  forbiddenPage,
  notFoundPage,
  serverErrorPage,
  maintenancePage,
  offlinePage,
} from './page-defs/system'

export const PAGE_DEFINITIONS: PageDefinition[] = [
  loginPage,
  dashboardPage,
  usersPage,
  familiesPage,
  regionsPage,
  medicationPlansPage,
  medicationRecordsPage,
  healthMonitoringPage,
  healthAlertsPage,
  alertRulesPage,
  healthReportsPage,
  careTeamPage,
  devicesPage,
  drugsPage,
  drugReviewPage,
  messagesPage,
  notificationTemplatesPage,
  contentTipsPage,
  contentFaqPage,
  membershipPlansPage,
  membershipOrdersPage,
  decorationPage,
  linksPage,
  assetsPage,
  supportTicketsPage,
  supportFeedbackPage,
  analyticsPage,
  adminUsersPage,
  rolesPage,
  generalSettingsPage,
  auditLogsPage,
  operationsPage,
  profilePage,
  forbiddenPage,
  notFoundPage,
  serverErrorPage,
  maintenancePage,
  offlinePage,
]

export function normalizePath(path: string): string {
  const withoutHash = path.startsWith('#') ? path.slice(1) : path
  return withoutHash.split('?')[0]?.replace(/\/+$/, '') || '/'
}

export function normalizeRoute(route: string): string {
  return route.startsWith('#') ? route : `#${route}`
}

export const PAGE_BY_ID = new Map(PAGE_DEFINITIONS.map(page => [page.id, page]))
export const PAGE_BY_PATH = new Map(PAGE_DEFINITIONS.map(page => [normalizePath(page.route), page]))

export function pageByPath(path: string): PageDefinition | undefined {
  return PAGE_BY_PATH.get(normalizePath(path))
}

export function pageById(id: string): PageDefinition | undefined {
  return PAGE_BY_ID.get(id)
}

export interface MenuGroup {
  title: string
  items: PageDefinition[]
}

/** Route order follows project.json. Group order is business-led, not alphabetical. */
const MENU_ORDER = [
  'dashboard',
  'users',
  'regions',
  'families',
  'medication-plans',
  'medication-records',
  'care-team',
  'health-monitoring',
  'health-alerts',
  'alert-rules',
  'health-reports',
  'devices',
  'drugs',
  'drug-review',
  'messages',
  'notification-templates',
  'content-tips',
  'content-faq',
  'membership-plans',
  'membership-orders',
  'decoration',
  'links',
  'assets',
  'support-tickets',
  'support-feedback',
  'analytics',
  'admin-users',
  'roles',
  'general-settings',
  'audit-logs',
  'operations',
]

const GROUP_ORDER = [
  '仪表盘',
  '用户管理',
  '家庭档案',
  '用药管理',
  '健康监测',
  '药品库',
  '消息通知',
  '运营中心',
  '客服与反馈',
  '数据分析',
  '系统设置',
]

export const MENU_PAGES: PageDefinition[] = MENU_ORDER
  .map(id => PAGE_BY_ID.get(id))
  .filter((page): page is PageDefinition => Boolean(page))

export const MENU_GROUPS: MenuGroup[] = GROUP_ORDER.map(title => ({
  title,
  items: MENU_PAGES.filter(page => page.group === title),
})).filter(group => group.items.length > 0)

export function accessibleMenu(has: (permission: string) => boolean): MenuGroup[] {
  return MENU_GROUPS.map(group => ({
    title: group.title,
    items: group.items.filter(page => has(page.permission)),
  })).filter(group => group.items.length > 0)
}

export function canAccessPage(page: PageDefinition, has: (permission: string) => boolean): boolean {
  if (page.permission === 'public') return true
  if (page.permission === 'authenticated') return has('authenticated')
  return has(page.permission)
}

export const PERMISSION_GROUPS: Array<{ module: string; label: string; permissions: string[] }> = Array.from(
  new Set(
    PAGE_DEFINITIONS.flatMap(page => {
      const values = [page.permission, ...Object.values(page.permissions)]
      return values.filter(permission => permission && permission !== 'public' && permission !== 'authenticated')
    }),
  ),
)
  .sort()
  .reduce<Array<{ module: string; label: string; permissions: string[] }>>((groups, permission) => {
    const module = permission.split('.')[0] || 'other'
    let group = groups.find(item => item.module === module)
    if (!group) {
      const label = PAGE_DEFINITIONS.find(page => page.permission.startsWith(`${module}.`))?.group ?? '其他权限'
      group = { module, label, permissions: [] }
      groups.push(group)
    }
    group.permissions.push(permission)
    return groups
  }, [])
